import { Button } from "@material-tailwind/react";
import { PencilSquareIcon } from "@heroicons/react/24/solid";
import { t } from "i18next";
import { useState } from "react";
import EditCreateDrawer from "../edit-create-drawer/EditCreateDrawer";
import CustomerForm, { CustomerFormValidator } from "../data/customer/CustomerForm";
import { useDrawerLayoutDispatch } from "../drawer-layout/DrawerLayout";
import { validator } from "../../services/form-validator/form-validator";
import toast from "react-hot-toast";
import { CustomerValidator } from "../data/customer/customer-validator";

/**
 * CustomerEditButton: bouton d'édition de customer avec fermeture de modal parent
 * @param customer - customer à éditer
 * @param onEditStart - callback appelée quand l'édition commence (pour fermer le modal parent)
 * @param onCustomerUpdate - callback appelée après mise à jour du customer
 */
/**
 * CustomerEditButton: bouton d'édition de customer
 *
 * `customer` must be the DETAILED customer (GET /users/customers/{id}). Seeding the form from a
 * CustomerSimpleResponse -- e.g. workorder.customer -- silently drops reportTemplate and
 * sendInterventionReportEmail, so both fields render empty and the user cannot tell an unset value
 * from a missing one. When the detail is not available the button is disabled rather than opening a
 * form on partial data.
 */
export default function CustomerEditButton({
  customer,
  onEditStart,
  onCustomerUpdate
}: {
  customer: EasyTech.Customer | null;
  onEditStart?: () => void;
  onCustomerUpdate: (updatedCustomer: EasyTech.Customer) => Promise<void>;
}) {
  const dispatch = useDrawerLayoutDispatch();
  
  const handleCustomerSubmit = async (updatedCustomer: EasyTech.Customer) => {
    await onCustomerUpdate(updatedCustomer);
  };

  // Composant wrapper pour gérer l'état du formulaire dans le drawer
  const DrawerFormWrapper = ({ initial }: { initial: EasyTech.Customer }) => {
    const [formData, setFormData] = useState(initial);
    const [submitted, setSubmitted] = useState(false);

    const formChange = (name: string, value: any) => {
      setFormData(prev => ({ ...prev, [name]: value }));
    };

    const submitForm = async () => {
      setSubmitted(true);

      // Validation spécialisée pour les customers
      const validationErrors = CustomerValidator.validate(formData, t);

      // Si des erreurs sont trouvées, afficher le message et bloquer la soumission
      if (validationErrors.length > 0) {
        const errorMessage = `${t("common:formError")}: ${validationErrors.join(", ")}`;
        toast.error(errorMessage);
        return;
      }

      if (!validator.isFormValid(formData, CustomerFormValidator)) {
        const fieldNames = {
          name: t("common:name"),
          activitySector: t("common:activitySector"),
          languages: t("common:languages"),
          nip: t("common:nip"),
          badPayer: t("common:badPayer"),
          contactInfo: t("common:contactInfo"),
          address: t("common:address")
        };

        const errorMessage = validator.getFormErrorMessage(formData, CustomerFormValidator, fieldNames, t);
        toast.error(errorMessage);
        return;
      }

      try {
        await handleCustomerSubmit(formData);
        dispatch({ type: "close" });
      } catch (error) {
        // L'erreur est déjà gérée dans handleCustomerSubmit
      }
    };

    return (
      <div className="drawer-form">
        <div className="content">
          <CustomerForm
            data={formData}
            onChange={formChange}
            onSubmit={submitForm}
            submitted={submitted}
          />
        </div>
        <div className="footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem", borderTop: "1px solid #e5e7eb" }}>
          <div className="text-xs text-gray-400 italic">{t("common:mandatoryField")}</div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button size="sm" variant="outlined" onClick={() => dispatch({ type: "close" })}>
              {t("common:cancel")}
            </Button>
            <Button size="sm" color="blue" onClick={submitForm}>
              {t("common:validate")}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const handleEditClick = () => {
    if (!customer) return;

    if (onEditStart) {
      // Fermer le modal parent
      onEditStart();
      
      // Ouvrir le drawer après un petit délai
      setTimeout(() => {
        dispatch({
          type: "open",
          title: t("Edit customer"),
          content: <DrawerFormWrapper initial={customer} />
        });
      }, 100);
    }
  };

  return (
    <div className="flex items-center">
      {onEditStart ? (
        <Button
          size="sm"
          color="blue"
          className="flex items-center gap-2 whitespace-nowrap normal-case font-normal"
          onClick={handleEditClick}
          disabled={!customer}
        >
          <PencilSquareIcon className="w-4 h-4" />
          {t("workorder:report:editCustomer")}
        </Button>
      ) : (
        <EditCreateDrawer
          data={customer ?? undefined}
          entityName="customer"
          FormComponent={CustomerForm}
          onSubmit={handleCustomerSubmit}
          isNotCreatable={true}
          disabled={!customer}
        />
      )}
    </div>
  );
} 